/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-14 00:00:00
 *
 *******************************************************/
import React, { Component } from 'react';
import { TextStyle, Button, Banner, ButtonGroup } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';

import { getShop, buildRedirectUrl, getThemeReviewUrl } from 'src/helpers';
import { navigateToShopifyCustomizeSSR } from 'src/helpers/shopify-integration-helpers';

import GuideISW from 'src/assets/images/shopify-integration/GuideISW.png';

type Props = {};

type ComposedProps = Props & WithI18nProps;

class ISWManual extends Component<ComposedProps> {
  state = {};

  render() {
    const { i18n, themeId, isOS20 = true } = this.props;

    return (
      <React.Fragment>
        <div className="heading">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuISW')}
        </div>

        {/* Banner warning */}
        {isOS20 ? this.renderWarningBannerOS20() : this.renderWarningBannerOS10()}

        <div className="instruction">
          {/* Step 1 */}
          <div className="step">
            <div style={{ display: 'inline-flex' }}>
              <div className="left-col">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.iswPageStep1')}
                </TextStyle>

                <div style={{ padding: '16px 0 0 0' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.iswPageStep1_instruction1', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.iswPageStep1_instruction1_t1'
                        )}
                      </TextStyle>
                    )
                  })}
                </div>

                <div style={{ padding: '16px 0' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.iswPageStep1_instruction2', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.ShopifyIntegration.Actions.save')}
                      </TextStyle>
                    )
                  })}
                </div>

                <div>
                  <Button
                    primary
                    onClick={() => {
                      navigateToShopifyCustomizeSSR(
                        themeId,
                        true,
                        process.env.REACT_APP_SHOPIFY_INTEGRATION_ISW_SSR_APP_PATH
                      );
                    }}>
                    {i18n.translate('AdminHTML.ShopifyIntegration.Actions.enableISW')}
                  </Button>
                </div>
              </div>
              {/* Illustration image */}
              <div className="right-col">
                <img
                  className="thumbnail clickable"
                  src={GuideISW}
                  width={500}
                  height={281}
                  alt=""
                  onClick={() => window.open(GuideISW, '_blank', 'noopener,noreferrer')}
                />
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="step">
            <div style={{ display: 'inline-flex' }}>
              <div className="left-col" style={{ width: '100%' }}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.iswPageStep2')}
                </TextStyle>

                <div style={{ padding: '16px 0' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.iswPageStep2_instruction', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.ShopifyIntegration.Actions.preview')}
                      </TextStyle>
                    )
                  })}
                </div>
                <div className="btnGroup">
                  <ButtonGroup>
                    {/* Change template */}
                    <Button
                      onClick={() => {
                        const previewUrl = getThemeReviewUrl(themeId);
                        window.open(previewUrl, '_blank');
                      }}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.preview')}
                    </Button>

                    {/* Customization */}
                    <Button
                      onClick={() => {
                        const redirectUrl = buildRedirectUrl(
                          getShop(),
                          routes.instantSearchWidget.view()
                        );
                        window.open(redirectUrl, '_blank');
                      }}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.gotoISWSettings')}
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderWarningBannerOS10 = () => {
    const { i18n, backToMainStep } = this.props;

    return (
      <div className="warning">
        <Banner status="critical">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warningInitialSetup', {
            t1: (
              <Button plain monochrome onClick={backToMainStep}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warningInitialSetup_t1')}
                </TextStyle>
              </Button>
            )
          })}
        </Banner>
      </div>
    );
  };

  renderWarningBannerOS20 = () => {
    const { i18n, backToMainStep } = this.props;

    return (
      <div className="warning">
        <Banner status="critical">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning', {
            t1: (
              <Button plain monochrome onClick={backToMainStep}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning_t1')}
                </TextStyle>
              </Button>
            ),
            t2: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warning_t2')}
              </TextStyle>
            )
          })}
        </Banner>
      </div>
    );
  };
}

export default withI18n()(ISWManual);
